import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch, API } from '../api';
import { fmtWh } from '../time';

// The Å shelves (Fernando 1.11, docs/PIECE-PLACES.md): the returned / cancelled Ä pieces lying on Å 1–3 against
// Inbound station 1, one row per piece. Managers put pieces there by hand (scan or type a barcode) and take them
// out; everyone can look. Picking takes a piece from here when an order needs that product (the last stop of the
// walk); the row is marked taken when the order is packed.

interface Place { code: string; display: string; note: string | null; pieces: number }
interface Piece {
  id: number; place: string; display: string; product_code: string | null; article_number: string | null; barcode: string | null;
  name: string | null; variant_title: string | null; size: string | null; picklist_number: string | null; stock_location: string | null;
  source: string; source_ref: string | null; note: string | null; placed_by: string | null; placed_at: string;
  reserved_order_id: string | null; reserved_round_id: number | null;
}
interface Lookup { product: any; candidate: boolean; propose: string | null }

const SOURCE: Record<string, string> = { return: 'returned', cancel: 'cancelled order', manual: 'by hand', inbound: 'inbound' };

export default function PiecesPage() {
  const navigate = useNavigate();
  const [places, setPlaces] = useState<Place[]>([]);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [filter, setFilter] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const [code, setCode] = useState('');
  const [found, setFound] = useState<Lookup | null>(null);
  const [place, setPlace] = useState('');
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/pieces${filter ? `?place=${encodeURIComponent(filter)}` : ''}`);
    if (!r.ok) { setErr('Could not load the shelves'); return; }
    const d = await r.json();
    setPlaces(d.places || []); setPieces(d.pieces || []); setCanManage(!!d.can_manage); setErr('');
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const lookup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setDone(''); setErr('');
    const r = await authFetch(`${API}/pieces/lookup?code=${encodeURIComponent(code.trim())}`);
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setFound(null); setErr(d.detail || 'No such product'); return; }
    setFound(d); setPlace(d.propose || places[0]?.code || '');
  };
  const add = async () => {
    if (!found || !place || busy) return;
    setBusy(true); setErr('');
    try {
      const r = await authFetch(`${API}/pieces/add`, { method: 'POST', body: JSON.stringify({ code: code.trim(), place, qty, note }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || 'Could not add'); return; }
      setDone(`${qty} × ${found.product.name || found.product.external_id} → ${places.find(p => p.code === place)?.display || place}`);
      setCode(''); setFound(null); setQty(1); setNote('');
      await load();
      codeRef.current?.focus();
    } finally { setBusy(false); }
  };
  const takeOut = async (p: Piece) => {
    const why = window.prompt(`Take ${p.picklist_number || p.product_code || 'this piece'} off ${p.display}? Why:`, 'taken out by hand');
    if (why === null) return;
    const r = await authFetch(`${API}/pieces/${p.id}/take-out`, { method: 'POST', body: JSON.stringify({ note: why }) });
    if (!r.ok) { const d = await r.json().catch(() => ({})); setErr(d.detail || 'Could not take it out'); return; }
    await load();
  };

  const total = places.reduce((a, p) => a + p.pieces, 0);
  return (
    <div className="p-6 text-gray-200 max-w-5xl mx-auto">
      <div className="flex items-baseline gap-4 mb-1">
        <h1 className="text-2xl font-bold text-white">Å shelves — returned pieces</h1>
        <button onClick={() => navigate('/settings/warehouse/tools')} className="text-sm text-gray-400 hover:text-white">← Warehouse tools</button>
      </div>
      <p className="text-gray-400 text-sm mb-5">A returned or cancelled piece whose product lives at Ä (or has no shelf) lies here, one row per piece, against the bottom shelves of Inbound station 1. Picking takes it from here as the last stop of the walk; the product's stock location and the shop's stock do not change.</p>
      {err && <div className="mb-3 text-red-400 text-sm">{err}</div>}

      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => setFilter(null)} className={`rounded-xl border px-5 py-3 text-left ${filter === null ? 'border-amber-500 bg-amber-900/20' : 'border-gray-800 bg-[#1a1d27]'}`}>
          <div className="text-3xl font-black">{total}</div><div className="text-xs text-gray-400">all shelves</div></button>
        {places.map(p => (
          <button key={p.code} onClick={() => setFilter(p.code)} className={`rounded-xl border px-5 py-3 text-left ${filter === p.code ? 'border-amber-500 bg-amber-900/20' : 'border-gray-800 bg-[#1a1d27]'}`}>
            <div className="text-3xl font-black">{p.pieces}</div><div className="text-sm font-semibold text-amber-200">{p.display}</div>
            {p.note && <div className="text-[11px] text-gray-500 max-w-[220px]">{p.note}</div>}
          </button>
        ))}
        {places.length === 0 && <div className="text-gray-500 text-sm">No Å shelf on the floor model yet — add them on the floor plan ("+ Add a shelf", picked on the way).</div>}
      </div>

      {canManage && (
        <div className="rounded-xl border border-gray-800 bg-[#1a1d27] p-4 mb-6">
          <div className="text-white font-semibold mb-2">Put pieces on a shelf</div>
          <form onSubmit={lookup} className="flex flex-wrap gap-2 items-center">
            <input ref={codeRef} value={code} onChange={e => setCode(e.target.value)} placeholder="Scan or type the barcode, variant id or article number" autoFocus
              className="flex-1 min-w-[280px] bg-gray-900 border border-gray-700 rounded px-3 py-2 text-white" />
            <button type="submit" className="px-4 py-2 rounded bg-gray-700 hover:bg-gray-600 text-white text-sm">Find</button>
          </form>
          {found && (
            <div className="mt-3 flex flex-wrap items-center gap-4">
              <div>
                <div className="text-white font-semibold">{found.product.name}{found.product.variant_title || found.product.size ? ` · ${found.product.variant_title || found.product.size}` : ''}</div>
                <div className="text-xs text-gray-400">{found.product.article_number || found.product.external_id} · lives at {found.product.stock_location || 'no shelf'}
                  {!found.candidate && <span className="text-amber-400"> · a product with its own shelf — only if you mean it</span>}</div>
              </div>
              <div className="flex gap-1">
                {places.map(p => <button key={p.code} type="button" onClick={() => setPlace(p.code)}
                  className={`w-16 h-12 rounded-lg text-lg font-black ${place === p.code ? 'bg-amber-500 text-black' : 'bg-gray-800 text-white'}`}>{p.display}</button>)}
              </div>
              <label className="text-sm text-gray-300">× <input type="number" min={1} max={200} value={qty} onChange={e => setQty(Math.max(1, Number(e.target.value) || 1))}
                className="w-16 bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white ml-1" /></label>
              <input value={note} onChange={e => setNote(e.target.value)} placeholder="why (optional)" className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-white text-sm w-48" />
              <button type="button" onClick={add} disabled={busy || !place} className="px-5 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-semibold">Put on the shelf</button>
            </div>
          )}
          {done && <div className="mt-2 text-emerald-300 text-sm">✓ {done}</div>}
        </div>
      )}

      <table className="w-full text-sm bg-[#1a1d27] border border-gray-800 rounded-lg overflow-hidden">
        <thead className="text-gray-500 text-left border-b border-gray-800">
          <tr><th className="py-2 px-3">Shelf</th><th className="py-2 px-3">Piece</th><th className="py-2 px-3">Came from</th><th className="py-2 px-3">Put there</th><th className="py-2 px-3">Kept for</th>{canManage && <th className="py-2 px-3"></th>}</tr>
        </thead>
        <tbody>
          {pieces.map(p => (
            <tr key={p.id} className="border-b border-gray-800/50">
              <td className="py-2 px-3 font-bold text-amber-200">{p.display}</td>
              <td className="py-2 px-3">
                <div className="text-white">{p.picklist_number ? <span className="font-semibold">{p.picklist_number} · </span> : null}{p.name || p.product_code}{p.variant_title || p.size ? ` · ${p.variant_title || p.size}` : ''}</div>
                <div className="text-xs text-gray-500">{p.article_number || p.product_code}{p.barcode ? ` · ${p.barcode}` : ''}{p.stock_location ? ` · product at ${p.stock_location}` : ''}</div>
              </td>
              <td className="py-2 px-3 text-gray-300">{SOURCE[p.source] || p.source}{p.note ? <div className="text-xs text-gray-500">{p.note}</div> : null}</td>
              <td className="py-2 px-3 text-gray-400 text-xs">{p.placed_by || '—'}<br />{fmtWh(p.placed_at, 'datetime')}</td>
              <td className="py-2 px-3 text-xs">{p.reserved_order_id ? <span className="text-blue-300">order {p.reserved_order_id}{p.reserved_round_id ? ` · round ${p.reserved_round_id}` : ''}</span> : <span className="text-gray-600">free</span>}</td>
              {canManage && <td className="py-2 px-3 text-right"><button onClick={() => takeOut(p)} className="px-3 py-1 rounded bg-gray-700 hover:bg-red-800 text-white text-xs">Take out</button></td>}
            </tr>
          ))}
          {pieces.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-gray-500">Nothing on {filter ? places.find(p => p.code === filter)?.display : 'the Å shelves'}.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
